import { useChannels } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { useProfile } from '@mint-lib/profile-helpers';
import { useQuery } from '@mint-lib/query-client';
import {
  OMNI_MDM_PLATFORMS,
  useMdmPlatformType,
} from '@mint-lib/routing-context';
import { Auth } from '@mint-sdk/api-auth';
import { ActivityPlatforms } from '@mint-sdk/api-data-ingestion';
import { Metadata } from '@mint-sdk/api-mdm';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import { Rows as MediaRow } from '@mint-sdk/api-media-row';

import * as API from '../api/index.js';
import type {
  ChannelCatalogItem,
  GranularityLevel,
  PlatformCatalogItem,
  UserInfo,
  UsersFilters,
} from '../api/types.js';
import {
  useActivityPlatformsV2FF,
  useImportHubV2,
  useMdmPlatformsV2FF,
  useWithConfiguredMediaRowsFF,
} from '../hooks/ff/index.js';
import { usePlatformsContainer, useTypedQuery } from '../hooks/index.js';
import { QueryKeys } from './queryClient.js';

const usersDefaultParams = {
  featureName: 'data_management_hub_feature',
  accessLevel: 'read_and_write_level',
};

export const useUsersInfo = (
  filters: UsersFilters,
  options?: { enabled?: boolean },
) => {
  return useTypedQuery({
    ...Auth.listUsersLite({ params: { ...usersDefaultParams, ...filters } }),
    select: (users) => ({
      list: users,
      byId: users.reduce(
        (acc, user) => {
          acc[user.id] = user;
          return acc;
        },
        {} as Record<string, UserInfo>,
      ),
    }),
    placeholderData: [] as UserInfo[],
    refetchOnMount: false,
    ...options,
  });
};

export const useMediaPlansListQuery = () => {
  const withConfiguredMediaRows = useWithConfiguredMediaRowsFF() || undefined;

  return useQuery({
    ...MediaPlan.getMediaPlansPreviewQueryOptions({
      params: { withConfiguredMediaRows },
    }),
  });
};

export const useJourneysListQuery = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: QueryKeys.resources.campaignsList,
    queryFn: API.getCampaignsList,
    ...options,
  });
};

export const useActivitiesListQuery = (
  mediaPlanId: undefined | null | string,
) => {
  const withConfiguredMediaRows = useWithConfiguredMediaRowsFF() || undefined;

  return useQuery({
    ...MediaPlan.getActivitiesPreviewQueryOptions({
      params: {
        mediaPlanId: mediaPlanId ?? '',
        withConfiguredMediaRows,
      },
    }),
    enabled: Boolean(mediaPlanId),
  });
};

export const usePlatformsByCampaign = (
  campaignID: number,
  options: { enabled?: boolean },
) => {
  const {
    data: { platformsByName },
    isPending: isPlatformsCatalogPending,
  } = usePlatformsCatalog();

  const platformsByCampaign = useTypedQuery({
    queryKey: QueryKeys.resources.platformsByCampaign(campaignID),
    queryFn: () => API.getPlatformsByCampaign(campaignID),
    placeholderData: [] as { name: string }[],
    ...options,
  });

  const isLoading = isPlatformsCatalogPending || platformsByCampaign.isPending;

  const data = platformsByCampaign.data.map(({ name }) => ({
    id: name,
    name: platformsByName[name]?.label,
  }));

  return { data, isLoading };
};

const mapPlatformsToOptions = (items: { name: string; label: string }[]) =>
  items.map(({ name, label }) => ({
    id: name,
    name: label,
  }));

export const usePlatformsOfActivity = (
  activityId: string,
  granularityLevel: GranularityLevel,
  options?: { enabled?: boolean },
) => {
  const { t } = useTranslation('@myn/import-hub');
  const isActivityPlatformsV2 = useActivityPlatformsV2FF();
  const isPricingItemLevel = granularityLevel === 'pricing_item';
  const withConfiguredMediaRows = useWithConfiguredMediaRowsFF() || undefined;
  const isQueryEnabled = Boolean(activityId) && (options?.enabled ?? true);

  const {
    data: platformsOfActivityV1,
    isPending: isPlatformsOfActivityV1Pending,
  } = useTypedQuery({
    ...MediaPlan.getActivityPlatformsQueryOptions({
      urlParams: { activityId: activityId ?? '' },
      params: {
        onlyVirtual: isPricingItemLevel,
        withConfiguredMediaRows,
      },
    }),
    select(data) {
      return mapPlatformsToOptions(data.items);
    },
    placeholderData: {
      items: [],
    },
    enabled: !isActivityPlatformsV2 && isQueryEnabled,
  });

  const {
    data: platformsOfActivityV2,
    isLoading: isPlatformsOfActivityV2Loading,
  } = useTypedQuery({
    ...ActivityPlatforms.getActivityPlatformsV2QueryOptions({
      urlParams: { activityId: activityId ?? '' },
      params: {
        granularityLevel,
      },
    }),
    select(data) {
      return mapPlatformsToOptions(data.items);
    },
    enabled: isActivityPlatformsV2 && isQueryEnabled,
  });

  const {
    data: { ghostPlatformsByName },
    isPending: isPlatformsCatalogPending,
  } = usePlatformsCatalogV2();

  const platformsOfActivity = isActivityPlatformsV2
    ? (platformsOfActivityV2 ?? [])
    : (platformsOfActivityV1 ?? []);

  const data =
    !isActivityPlatformsV2 && isPricingItemLevel
      ? platformsOfActivity.filter((platform) => {
          return Boolean(ghostPlatformsByName[platform.id]);
        })
      : platformsOfActivity;

  const isLoading = isActivityPlatformsV2
    ? isPlatformsOfActivityV2Loading
    : [
        isPlatformsOfActivityV1Pending,
        isPricingItemLevel && isPlatformsCatalogPending,
      ].some(Boolean);

  const noOptionsText = isActivityPlatformsV2
    ? t(
        'There are no available platforms for the selected Activity and Data attribution level',
      )
    : undefined;

  return { data, isLoading, noOptionsText };
};

export const useAllPlatformsList = (params: {
  campaignCollectionId?: null | number;
  channelName?: null | string;
}) => {
  return useQuery({
    queryKey: QueryKeys.resources.allPlatformsList(params),
    queryFn: () => API.getAllPlatformsList(params),
    refetchOnMount: false,
  });
};

export const useChannelNamesList = (
  params: {
    campaignCollectionId?: null | number;
    platformName?: null | string;
    tileId?: null | string;
  },
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: QueryKeys.resources.channelNamesList(params),
    queryFn: () => API.getChannelNamesList(params),
    refetchOnMount: false,
    ...options,
  });
};

export const useActivityChannelNamesList = (
  activityId: string,
  params: {
    platformName?: null | string;
    mediaRowItemId?: null | string;
  },
) => {
  return useQuery({
    ...MediaPlan.getActivityChannelNamesQueryOptions({
      urlParams: { activityId: activityId },
      params: {
        platformName: params.platformName,
        mediaRowItemId: params.mediaRowItemId,
      },
    }),
    refetchOnMount: false,
  });
};

export const useMediaRowslist = (params: {
  campaignCollectionId: number;
  platformName?: null | string;
  channelName?: null | string;
}) => {
  const queryOptions = MediaRow.getMediaRowsListQueryOptions({
    params: {
      ...params,
      withDates: true,
    },
  });

  return useQuery({
    ...queryOptions,
  });
};

export const useActivityMediaRowsList = (
  activityId: string,
  params: {
    platformName?: null | string;
    channelName?: null | string;
  },
  options?: ReturnType<typeof MediaPlan.getActivityMediaRowsQueryOptions>,
) => {
  return useQuery({
    ...MediaPlan.getActivityMediaRowsQueryOptions({
      urlParams: { activityId: activityId },
      params: {
        platformName: params.platformName,
        channelName: params.channelName,
      },
    }),
    ...options,
  });
};

export const useMediaRowNamesByIdV1 = (
  campaignCollectionId: number,
  options?: { enabled?: boolean },
) => {
  const queryOptions = MediaRow.getMediaRowsListQueryOptions({
    params: {
      campaignCollectionId,
      withDates: true,
    },
  });

  return useTypedQuery({
    ...queryOptions,
    select(data) {
      return data.reduce(
        (acc, mediaRow) => {
          acc[mediaRow.tileId] = mediaRow.name;
          return acc;
        },
        {} as Record<string, string>,
      );
    },
    placeholderData: [],
    ...options,
  });
};

export const useMediaRowNamesByIdV2 = (
  activityId: string,
  options?: { enabled?: boolean },
) => {
  return useTypedQuery({
    ...MediaPlan.getActivityMediaRowsQueryOptions({
      urlParams: { activityId },
      params: {},
    }),
    select(data) {
      if (!data) {
        return {};
      }

      return data.reduce(
        (acc, mediaRow) => {
          acc[mediaRow.mediaItemId] = mediaRow.name;
          return acc;
        },
        {} as Record<string, string>,
      );
    },
    placeholderData: [],
    ...options,
  });
};

export const useMediaRowNamesById = (
  params: { campaignCollectionId?: number; activityId?: string },
  options?: { enabled?: boolean },
) => {
  const isImportHubV2 = useImportHubV2();

  if (isImportHubV2) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMediaRowNamesByIdV2(params.activityId as string, {
      ...options,
    });
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMediaRowNamesByIdV1(params.campaignCollectionId as number, {
    ...options,
  });
};

export const useExampleCsvFile = () => {
  return useQuery({
    queryKey: QueryKeys.resources.exampleCsvFile(),
    queryFn: () => API.getExampleCsvFile(),
  });
};

export const useChannelsCatalog = () => {
  const isImportHubV2 = useImportHubV2();

  if (isImportHubV2) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useChannelsCatalogV2();
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useChannelsCatalogV1();
};

export const useChannelsCatalogV1 = () => {
  const staticChannelsByName = useChannels();

  return useTypedQuery({
    queryKey: ['channelsCatalog'],
    queryFn: () => {
      const channelsByName = Object.fromEntries(
        Object.entries(staticChannelsByName).map(([name, channel]) => [
          name,
          {
            label: channel.name,
            logo: channel.icon,
            type: channel.type,
          } satisfies ChannelCatalogItem,
        ]),
      );

      return { channelsByName };
    },
    placeholderData: { channelsByName: {} },
  });
};

export const useChannelsCatalogV2 = () => {
  const { profile } = useProfile();

  const fieldUrnId = 'urn:mint:field:channel_v2' as const;

  return useTypedQuery({
    ...Metadata.getContextMetadataQueryOptions({
      params: {
        fieldUrnIds: [fieldUrnId],
        context: 'import_hub',
        companyId: profile?.profile.company.id,
        includeDisabled: false,
      },
    }),
    select(data) {
      const channelsByName = data[fieldUrnId].options.reduce(
        (acc, channel) => {
          acc[channel.value] = {
            label: channel.label,
            logo: null,
            type: null,
          };
          return acc;
        },
        {} as Record<string, ChannelCatalogItem>,
      );

      return { channelsByName };
    },
    placeholderData: {
      [fieldUrnId]: {
        dataTypeProperty: {},
        formula: null,
        defaultValue: null,
        isDisabled: false,
        label: 'Channel',
        options: [],
        type: 'select',
      },
    } as const,
    enabled: Boolean(profile?.profile.company.id),
    staleTime: 30 * 1000,
  });
};

export const usePlatformsCatalog = () => {
  const isImportHubV2 = useImportHubV2();

  if (isImportHubV2) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return usePlatformsCatalogV2();
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return usePlatformsCatalogV1();
};

export const usePlatformsCatalogV1 = () => {
  return usePlatformsContainer();
};

const omniPlatformsByName = OMNI_MDM_PLATFORMS.reduce(
  (acc, platform) => {
    acc[`urn:mint:value:platform_v2:${platform}`] = true;
    return acc;
  },
  {} as Record<string, boolean>,
);

export const usePlatformsCatalogV2 = () => {
  const { profile } = useProfile();
  const isMdmPlatformsV2Enabled = useMdmPlatformsV2FF();
  const {
    isOmniPlatform,
    isAdServerPlatform,
    isAnalyticsPlatform,
    isLoading: isPlatformTypeLoading,
  } = useMdmPlatformType({
    enabled: isMdmPlatformsV2Enabled,
  });

  const fieldUrnId = 'urn:mint:field:platform_v2' as const;

  const platformsCatalogQuery = useTypedQuery({
    ...Metadata.getContextMetadataQueryOptions({
      params: {
        fieldUrnIds: [fieldUrnId],
        context: 'import_hub',
        companyId: profile?.profile.company.id,
      },
    }),
    select(data) {
      const platformOptions = data[fieldUrnId].options;

      const platformsList = platformOptions
        .filter((option) => !option.isDisabled)
        .map((option) => ({
          name: option.value,
          label: option.label,
          logo: null,
          isIntegrated: true,
          type: null,
        })) satisfies PlatformCatalogItem[];

      const platformsByName = platformsList.reduce(
        (acc, platform) => {
          acc[platform.name] = platform;
          return acc;
        },
        {} as Record<string, PlatformCatalogItem>,
      );

      const ghostPlatforms = platformsList.filter((platform) => {
        if (!isMdmPlatformsV2Enabled) {
          return !omniPlatformsByName[platform.name];
        }

        return !(
          isOmniPlatform(platform.name) ||
          isAdServerPlatform(platform.name) ||
          isAnalyticsPlatform(platform.name)
        );
      });

      const ghostPlatformsByName = ghostPlatforms.reduce(
        (acc, platform) => {
          acc[platform.name] = platform;
          return acc;
        },
        {} as Record<string, PlatformCatalogItem>,
      );

      return {
        platformsList,
        integratedPlatforms: platformsList,
        platformsByName,
        ghostPlatformsByName,
      };
    },
    placeholderData: {
      [fieldUrnId]: {
        dataTypeProperty: {},
        formula: null,
        defaultValue: null,
        isDisabled: false,
        label: 'Platform',
        options: [],
        type: 'select',
      },
    } as const,
    enabled: Boolean(profile?.profile.company.id),
    staleTime: 30 * 1000,
  });

  return {
    data: platformsCatalogQuery.data,
    isPending:
      platformsCatalogQuery.isPending ||
      (isMdmPlatformsV2Enabled && isPlatformTypeLoading),
  };
};
